import { pickShadowing } from '@engpod/core';
import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../components/Icon';
import { prefs } from '../lib/local';
import { useRecorder } from '../lib/useRecorder';
import type { SegmentView } from '../types';
import type { StepProps } from './context';

/**
 * 跟读: five representative sentences, one card at a time — listen (normal / slow),
 * record, play yourself back, or compare original → you.
 */
export function Shadowing({ detail, state, complete, playSegment, setMode, setRate, rate }: StepProps) {
  const picks = useMemo(
    () =>
      pickShadowing(
        detail.segments,
        detail.lesson.expressions.map((e) => e.segmentId).filter((x): x is string => Boolean(x)),
        state.hardSegments ?? [],
      ),
    [detail, state.hardSegments],
  );
  const [k, setK] = useState(0);
  const [showZh, setShowZh] = useState(() => prefs.get<boolean>('shadowZh', true));
  const [done, setDone] = useState<Set<string>>(new Set());
  useEffect(() => setMode('none'), [setMode]);
  const seg = picks[k];
  if (!seg) return <p className="muted">这一集还没有可跟读的句子。</p>;

  return (
    <div className="step-body shadowing">
      <div className="dots" aria-label={`第 ${k + 1} / ${picks.length} 句`}>
        {picks.map((p, n) => (
          <button key={p.id} className={`dot${n === k ? ' on' : ''}${done.has(p.id) ? ' done' : ''}`} onClick={() => setK(n)} aria-label={`第 ${n + 1} 句`} />
        ))}
        <small>
          精华句 {k + 1} / {picks.length}
        </small>
      </div>
      <ShadowCard
        key={seg.id}
        seg={seg}
        showZh={showZh}
        onToggleZh={() => {
          setShowZh(!showZh);
          prefs.set('shadowZh', !showZh);
        }}
        play={(slow) => {
          const prev = rate;
          if (slow) setRate(0.75);
          else if (rate < 1) setRate(1);
          playSegment(seg);
          return ((seg.endMs - seg.startMs) / (slow ? 0.75 : prev < 1 ? 1 : prev)) + 400;
        }}
        onRecorded={() => setDone((d) => new Set(d).add(seg.id))}
      />
      <div className="row spread">
        <button className="plain small" disabled={k === 0} onClick={() => setK(k - 1)}>
          ← 上一句
        </button>
        {k < picks.length - 1 ? (
          <button className="primary" onClick={() => setK(k + 1)}>
            下一句 →
          </button>
        ) : (
          <button className="primary" onClick={() => complete('shadowing')}>
            完成跟读 ✓
          </button>
        )}
      </div>
      <small className="muted">选句：本集核心表达所在的句子，加上你精听时觉得难的句子。录音只留在本次学习中，不会上传。</small>
    </div>
  );
}

function ShadowCard({ seg, showZh, onToggleZh, play, onRecorded }: { seg: SegmentView; showZh: boolean; onToggleZh(): void; play(slow: boolean): number; onRecorded(): void }) {
  const rec = useRecorder();
  const [comparing, setComparing] = useState(false);
  useEffect(() => {
    if (rec.state === 'ready') onRecorded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.state]);
  const compareNow = async () => {
    setComparing(true);
    const ms = play(false);
    await new Promise((r) => setTimeout(r, ms));
    await rec.play();
    setComparing(false);
  };
  return (
    <article className="shadow-card">
      <p className="shadow-en">{seg.en}</p>
      {showZh && seg.zh && <p className="shadow-zh">{seg.zh}</p>}
      <button className="plain small zh-toggle" onClick={onToggleZh}>
        {showZh ? '隐藏中文' : '显示中文'}
      </button>
      <div className="shadow-listen">
        <button onClick={() => play(false)}>
          <Icon name="volume" /> 原声
        </button>
        <button onClick={() => play(true)}>🐢 慢速</button>
      </div>
      <div className="shadow-rec">
        {rec.state === 'recording' ? (
          <button className="rec-big on" onClick={rec.stop} aria-label="停止录音">
            <span className="rec-square" />
          </button>
        ) : (
          <button className="rec-big" onClick={() => void rec.start()} aria-label="录音">
            <Icon name="mic" />
          </button>
        )}
        <small>
          {rec.state === 'recording' ? `录音中 ${rec.seconds}s · 再点一下停止` : rec.state === 'ready' ? '录好了，听听像不像' : rec.state === 'denied' ? '无法使用麦克风，请在设置里允许' : '听完原声，点一下开始跟读'}
        </small>
      </div>
      {rec.state === 'ready' && (
        <div className="shadow-after">
          <button onClick={() => void rec.play()}>▶ 我的</button>
          <button className="primary" disabled={comparing} onClick={() => void compareNow()}>
            {comparing ? '对比中…' : '⇄ 对比：原声 → 我的'}
          </button>
          <button className="plain" onClick={rec.reset}>
            重录
          </button>
        </div>
      )}
    </article>
  );
}
